// WelcomeDashboard — simplified Phase 3
// Entry point after login. Shows learning paths diagram or get-started CTA.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import Layout from '../layout/Layout';
import Card from '../ui/Card';
import AnnaGlow from '../ui/AnnaGlow';
import LearningPathsDiagram from './LearningPathsDiagram';

const getFirstName = (displayName: string | null | undefined, fallback: string): string => {
  if (!displayName) return fallback;
  return displayName.split(' ')[0] || fallback;
};

const WelcomeDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { journeys, loading } = useLearningJourneys();

  const firstName = getFirstName(user?.displayName, t('welcome.defaultUser'));
  const hasJourneys = journeys.length > 0;

  return (
    <Layout>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg">
        {tCommon('accessibility.skipToContent')}
      </a>

      <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
      <div id="main-content" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" tabIndex={-1}>
        {/* Welcome header */}
        <div className="mb-8">
          <h1 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white mb-2">
            {t('welcome.title', { firstName })}
          </h1>
          <p className="text-neutral-medium dark:text-gray-400 text-sm">
            {t('welcome.subtitle')}
          </p>
        </div>

        {/* Anna greeting — compact */}
        <Card padding="md" className="mb-8" showAccent>
          <div className="flex items-center gap-4">
            <AnnaGlow size="sm" />
            <div>
              <h2 className="text-lg font-display font-semibold text-neutral-dark dark:text-white mb-1">
                {t('anna.greeting', { firstName })}
              </h2>
              <p className="text-sm text-neutral-medium dark:text-gray-300">
                {t('anna.description')}
              </p>
              <p className="text-sm text-neutral-medium dark:text-gray-300 mt-1">
                {t('anna.availability')}
              </p>
            </div>
          </div>
        </Card>

        {/* Content: loading, empty state, or learning paths diagram */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : !hasJourneys ? (
          /* Empty state — create first journey */
          <Card hover onClick={() => navigate('/journeys/new')} padding="lg" className="cursor-pointer group mb-8">
            <div className="flex items-center gap-6">
              <span className="text-4xl group-hover:scale-110 transition-transform">+</span>
              <div>
                <h3 className="text-lg font-display font-semibold text-neutral-dark dark:text-white mb-1">
                  {t('getStarted.createJourney.title')}
                </h3>
                <p className="text-neutral-medium dark:text-gray-400">
                  {t('getStarted.createJourney.description')}
                </p>
              </div>
            </div>
          </Card>
        ) : (
          /* Learning paths info box */
          <Card padding="lg" className="mb-8">
            <h2 className="text-lg font-display font-semibold text-neutral-dark dark:text-white mb-4">
              {t('learningPaths.title')}
            </h2>
            <LearningPathsDiagram />
          </Card>
        )}

        {/* Navigation to main areas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {([
            { key: 'teaching', path: '/sessions', icon: '🎙️' },
            { key: 'preparation', path: '/preparation', icon: '✍️' },
          ] as const).map(({ key, path, icon }) => (
            <Card
              key={key}
              hover
              padding="md"
              className="cursor-pointer text-center group"
              onClick={() => navigate(path)}
            >
              <span className="text-3xl mb-2 block group-hover:scale-110 transition-transform">{icon}</span>
              <h3 className="font-display font-semibold text-neutral-dark dark:text-white mb-1">
                {t(`navigation.${key}.title`)}
              </h3>
              <p className="text-sm text-neutral-medium dark:text-gray-400">
                {t(`navigation.${key}.description`)}
              </p>
            </Card>
          ))}
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default WelcomeDashboard;
